import { useEffect } from "react";

/**
 * Eagerly imports page chunks in the background after the initial page renders.
 * This warms the browser's module cache so React.lazy() resolves instantly
 * on first navigation — eliminating the loading spinner between pages.
 *
 * Only triggers once per session. Chunks are downloaded with low priority
 * after a short delay to avoid competing with the initial page load.
 */

let prefetched = false;

// Priority tiers — most-used pages first, admin pages last
const TIER_1_DELAY = 1500; // Core pages — prefetch quickly
const TIER_2_DELAY = 4000; // Secondary pages
const TIER_3_DELAY = 8000; // Admin / rarely visited

function prefetchTier(imports: Array<() => Promise<unknown>>) {
  imports.forEach((imp) => {
    imp().catch(() => {
      // Silently ignore — chunk will load normally on navigation
    });
  });
}

export function ChunkPrefetcher() {
  useEffect(() => {
    if (prefetched) return;
    prefetched = true;

    // Tier 1: Core pages that most users visit
    const t1 = setTimeout(() => {
      prefetchTier([
        () => import("@/pages/app/roster"),
        () => import("@/pages/app/messages"),
        () => import("@/pages/app/clock"),
        () => import("@/pages/app/requests"),
        () => import("@/pages/app/profile"),
        () => import("@/pages/staff/StaffRoster"),
        () => import("@/pages/staff/StaffMessages"),
        () => import("@/pages/staff/StaffClock"),
        () => import("@/pages/staff/StaffDashboard"),
      ]);
    }, TIER_1_DELAY);

    // Tier 2: Secondary pages
    const t2 = setTimeout(() => {
      prefetchTier([
        () => import("@/pages/app/calendar"),
        () => import("@/pages/app/meetings"),
        () => import("@/pages/app/projects"),
        () => import("@/pages/app/tasks"),
        () => import("@/pages/app/training"),
        () => import("@/pages/app/availability"),
        () => import("@/pages/app/notifications"),
        () => import("@/pages/app/feedback"),
        () => import("@/pages/app/reminders"),
        () => import("@/pages/app/procedures"),
        () => import("@/pages/staff/StaffProfile"),
        () => import("@/pages/staff/StaffCalendar"),
        () => import("@/pages/staff/StaffMeetings"),
        () => import("@/pages/staff/StaffTasks"),
        () => import("@/pages/staff/StaffProjects"),
        () => import("@/pages/staff/StaffTraining"),
        () => import("@/pages/staff/StaffProcedures"),
        () => import("@/pages/staff/StaffTimesheet"),
        () => import("@/pages/staff/StaffSchedule"),
        () => import("@/pages/staff/StaffSettings"),
      ]);
    }, TIER_2_DELAY);

    // Tier 3: Admin pages
    const t3 = setTimeout(() => {
      prefetchTier([
        () => import("@/pages/staff/StaffConfiguration"),
        () => import("@/pages/staff/admin/StaffAdminRoster"),
        () => import("@/pages/staff/admin/StaffAdminStaff"),
        () => import("@/pages/staff/admin/StaffAdminAvailability"),
        () => import("@/pages/staff/admin/StaffAdminLeave"),
        () => import("@/pages/staff/admin/StaffAdminShiftSwaps"),
        () => import("@/pages/staff/admin/StaffAdminRequests"),
        () => import("@/pages/staff/admin/StaffAdminReports"),
        () => import("@/pages/staff/admin/StaffAdminDetail"),
        () => import("@/pages/app/roster-create"),
        () => import("@/pages/app/staff"),
      ]);
    }, TIER_3_DELAY);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  return null;
}
